/* RoRitmo product page (sections/rr-product-main.liquid) */
(() => {
  if (customElements.get('rr-pdp')) return;

  function formatMoney(cents, format) {
    const fixed = (cents / 100).toFixed(2);
    const [whole, dec] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amount = dec === '00' ? grouped : `${grouped}.${dec}`;
    const template = format || '{{amount}}';
    return template.replace(/\{\{\s*\w+\s*\}\}/, amount).replace(/<[^>]*>/g, '');
  }

  class RRPdp extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-rr-json]');
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.product = this.data.products[0];
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[data-rr-variant-id]');
      this.variantSelect = this.querySelector('[data-rr-variant]');
      this.qtyInput = this.querySelector('[data-rr-qty]');
      this.planInput = this.querySelector('[data-rr-plan]');
      this.addButton = this.querySelector('[data-rr-add]');
      this.errorEl = this.querySelector('[data-rr-error]');

      this.initGallery();
      this.initDropdowns();
      this.initTabs();
      this.initFbt();
      this.initSticky();

      this.variantSelect?.addEventListener('change', () => this.onVariantChange());
      this.querySelector('[data-rr-flavour]')?.addEventListener('change', (e) => this.onFlavourChange(e));
      this.querySelectorAll('[data-rr-qty-step]').forEach((btn) =>
        btn.addEventListener('click', () => {
          const next = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + parseInt(btn.dataset.rrQtyStep, 10));
          this.qtyInput.value = next;
          this.updateAddLabel();
        })
      );
      this.qtyInput?.addEventListener('change', () => {
        this.qtyInput.value = Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
        this.updateAddLabel();
      });
      this.querySelectorAll('[data-rr-plan-btn]').forEach((btn) =>
        btn.addEventListener('click', () => this.onPlanChange(btn))
      );
      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addItems([this.mainItem()], false, this.addButton);
      });
      this.querySelector('[data-rr-buy-now]')?.addEventListener('click', (e) =>
        this.addItems([this.mainItem()], true, e.currentTarget)
      );
      this.initRatingLine();
      this.initTaglines();
      // "Pack plans" cards further down the page ask for a pack by its number (see snippets/rr-gift-key.liquid)
      document.addEventListener('rr:select-pack', (e) => this.selectPack(e.detail && e.detail.key));
      this.querySelectorAll('[data-rr-reviews-link]').forEach((link) =>
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const target =
            document.querySelector('[id^="RRReviews-"]') ||
            document.querySelector('[id^="pdp-reviews-"]') ||
            document.querySelector('.jdgm-review-widget, #judgeme_product_reviews');
          target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        })
      );
    }

    /* Select the pack whose number matches (prefer one in stock), then scroll back up to the product */
    selectPack(key) {
      if (!key) return;
      const v =
        this.product.variants.find((x) => x.giftKey === key && x.available) ||
        this.product.variants.find((x) => x.giftKey === key);
      if (v && String(v.id) !== String(this.variantInput.value)) {
        const select = this.variantSelect;
        const dd = select && select.closest('[data-rr-dd]');
        const option = dd && dd.querySelector(`[role="option"][data-value="${v.id}"]`);
        if (option) {
          this.chooseOption(dd, option);
        } else if (select) {
          select.value = v.id;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      const header = parseInt(getComputedStyle(document.body).getPropertyValue('--height-header'), 10) || 100;
      const top = this.getBoundingClientRect().top + window.scrollY - header - 12;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      // Briefly highlight the pack picker so the change is noticed
      const box = select && select.closest('[data-rr-dd]');
      if (box) {
        box.classList.add('is-flash');
        setTimeout(() => box.classList.remove('is-flash'), 1600);
      }
    }

    /* Taglines under the description change one by one */
    initTaglines() {
      const box = this.querySelector('[data-rr-tagline]');
      if (!box || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const lines = [...box.querySelectorAll('.rr-tagline__line')];
      if (lines.length < 2) return;
      const interval = Math.max(1500, Number(box.dataset.interval) || 3000);
      let i = 0;
      setInterval(() => {
        lines[i].classList.remove('is-active');
        lines[i].setAttribute('aria-hidden', 'true');
        i = (i + 1) % lines.length;
        lines[i].classList.add('is-active');
        lines[i].removeAttribute('aria-hidden');
      }, interval);
    }

    /* Rating line under the price: average and count from the RoRitmo reviews section on this page */
    initRatingLine() {
      const line = this.querySelector('[data-rr-rating-line]');
      const reviews = document.querySelector('[data-rrr-count]');
      if (!line) return;
      // A "Review total" block in the product section overrides the count (and optionally the average) per product
      const count = parseInt(line.dataset.count, 10) || (reviews ? parseInt(reviews.dataset.rrrCount, 10) : 0) || 0;
      const avg = Math.min(5, parseFloat(line.dataset.avg) || (reviews ? parseFloat(reviews.dataset.rrrAvg) : 0) || 0);
      if (!count || !avg) return;
      const full = Math.round(avg);
      const stars = [1, 2, 3, 4, 5].map((i) => `<span${i > full ? ' class="is-empty"' : ''}>★</span>`).join('');
      line.innerHTML = `<span class="rr-stars" aria-label="${avg.toFixed(1)} out of 5">${stars}</span> <strong>${avg.toFixed(1)}</strong> <span>· ${count.toLocaleString('en-IN')} review${count === 1 ? '' : 's'}</span>`;
      line.hidden = false;
      const fallback = this.querySelector('[data-rr-rating-fallback]');
      if (fallback) fallback.hidden = true;

      // Keep the reviews section's summary in step with the product's "Review total"
      if (reviews && (line.dataset.count || line.dataset.avg)) {
        const avgText = reviews.querySelector('[data-rrr-avg-text]');
        const starsEl = reviews.querySelector('[data-rrr-stars]');
        const countText = reviews.querySelector('[data-rrr-count-text]');
        if (avgText) avgText.textContent = avg.toFixed(1);
        if (starsEl) {
          starsEl.setAttribute('aria-label', `${avg.toFixed(1)} out of 5`);
          starsEl.querySelectorAll('span').forEach((s, i) => s.classList.toggle('is-empty', i + 1 > full));
        }
        if (countText) {
          const label = countText.dataset.label || 'review';
          countText.textContent = `${count.toLocaleString('en-IN')} ${label}${count === 1 ? '' : 's'}`;
        }
        // The five-star share is worked out from the cards on the page, so it wouldn't match the total
        const pct5 = reviews.querySelector('[data-rrr-pct5]');
        if (pct5) pct5.hidden = true;
      }
    }

    get variant() {
      const id = Number(this.variantInput.value);
      return this.product.variants.find((v) => v.id === id) || this.product.variants[0];
    }

    money(cents) {
      return formatMoney(cents, this.data.moneyFormat);
    }

    mainItem() {
      const item = { id: this.variant.id, quantity: parseInt(this.qtyInput.value, 10) || 1 };
      if (this.planInput && !this.planInput.disabled && this.planInput.value) {
        item.selling_plan = Number(this.planInput.value);
      }
      return item;
    }

    /* ---------- Variant / flavour ---------- */
    onVariantChange() {
      this.variantInput.value = this.variantSelect.value;
      const v = this.variant;

      this.querySelector('[data-rr-price]').textContent = this.money(v.price);
      const compare = this.querySelector('[data-rr-compare]');
      if (compare) {
        compare.hidden = !(v.compare > v.price);
        compare.textContent = this.money(v.compare);
      }
      const perGlass = this.querySelector('[data-rr-per-glass]');
      if (perGlass) {
        perGlass.hidden = perGlass.hasAttribute('data-off') || !v.sachets || v.compare > v.price;
        if (v.sachets) perGlass.textContent = `(${this.money(Math.round(v.price / v.sachets / 100) * 100)} / glass)`;
      }
      const infoLine = this.querySelector('[data-rr-info-line]');
      if (infoLine) {
        infoLine.textContent = v.sachets
          ? infoLine.dataset.template.replace('[sachets]', v.sachets)
          : infoLine.dataset.fallback;
      }

      [this.addButton, this.querySelector('[data-rr-buy-now]'), this.querySelector('[data-rr-sticky-add]')]
        .filter(Boolean)
        .forEach((btn) => (btn.disabled = !v.available));
      this.updateAddLabel();

      // Gift banner follows the pack: text for its pack number (see snippets/rr-gift-key.liquid), hidden if none
      const gift = this.querySelector('[data-rr-gift]');
      if (gift && this.data.giftRules) {
        const text = this.data.giftRules[String(v.giftKey)] || '';
        gift.querySelector('[data-rr-gift-text]').textContent = text;
        gift.hidden = !text;
      }

      const hasOwnImages = this.showGroupFor(v.id);
      if (!hasOwnImages && v.media) this.goToMedia(v.media);
      this.syncFbtVariants(v.sachets);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
    }

    updateAddLabel() {
      const v = this.variant;
      const qty = parseInt(this.qtyInput.value, 10) || 1;
      const label = this.querySelector('[data-rr-add-label]');
      if (label) label.textContent = v.available ? `${this.money(v.price * qty)} | add to cart` : 'sold out';
      const sticky = this.querySelector('[data-rr-sticky-price]');
      if (sticky) sticky.textContent = this.money(v.price);
    }

    onFlavourChange(e) {
      const option = e.target.selectedOptions[0];
      const target = this.data.products.find((p) => String(p.id) === option.dataset.productId);
      let url = e.target.value;
      if (target) {
        const match = target.variants.find((v) => v.available && v.sachets && v.sachets === this.variant.sachets);
        if (match) url += `${url.includes('?') ? '&' : '?'}variant=${match.id}`;
      }
      window.location.href = url;
    }

    onPlanChange(btn) {
      this.querySelectorAll('[data-rr-plan-btn]').forEach((b) => {
        const active = b === btn;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', String(active));
      });
      if (this.planInput) this.planInput.disabled = !btn.dataset.rrPlanBtn;
    }

    /* ---------- Gallery ---------- */
    initGallery() {
      this.track = this.querySelector('[data-rr-track]');
      this.thumbsWrap = this.querySelector('[data-rr-thumbs]');
      if (!this.track) return;
      this.thumbsWrap?.addEventListener('click', (e) => {
        const thumb = e.target.closest('.rr-gallery__thumb');
        if (thumb) this.goToSlide(this.visibleThumbs().indexOf(thumb));
      });
      let ticking = false;
      this.track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          this.markThumb(Math.round(this.track.scrollLeft / this.track.clientWidth));
          ticking = false;
        });
      });
      this.refreshThumbs();
    }

    visibleSlides() {
      return [...this.track.querySelectorAll('.rr-gallery__slide:not([hidden])')];
    }

    visibleThumbs() {
      return this.thumbsWrap ? [...this.thumbsWrap.querySelectorAll('.rr-gallery__thumb:not([hidden])')] : [];
    }

    refreshThumbs() {
      const thumbs = this.visibleThumbs();
      if (this.thumbsWrap) this.thumbsWrap.hidden = thumbs.length < 2;
      this.markThumb(0);
    }

    markThumb(index) {
      this.visibleThumbs().forEach((t, i) => t.classList.toggle('is-active', i === index));
    }

    goToSlide(index) {
      if (index < 0) return;
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
      this.markThumb(index);
    }

    /* Show the selected variant's own images (custom.variant_images), else the product media. */
    showGroupFor(variantId) {
      if (!this.track) return false;
      const id = String(variantId);
      const hasOwn = !!this.track.querySelector(`.rr-gallery__slide[data-rr-group="${id}"]`);
      const group = hasOwn ? id : 'default';
      const current = this.track.querySelector('.rr-gallery__slide:not([hidden])')?.dataset.rrGroup;
      if (current === group) return hasOwn;
      this.querySelectorAll('.rr-gallery__slide, .rr-gallery__thumb').forEach((el) => {
        el.hidden = el.dataset.rrGroup !== group;
      });
      this.track.scrollLeft = 0;
      this.refreshThumbs();
      return hasOwn;
    }

    goToMedia(mediaId) {
      const index = this.visibleSlides().findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (index > -1) this.goToSlide(index);
    }

    /* ---------- Custom dropdowns (drive the hidden native selects) ---------- */
    initDropdowns() {
      this.dropdowns = [...this.querySelectorAll('[data-rr-dd]')].filter((dd) => dd.querySelector('[data-rr-dd-toggle]'));
      this.dropdowns.forEach((dd) => {
        const toggle = dd.querySelector('[data-rr-dd-toggle]');
        const menu = dd.querySelector('[data-rr-dd-menu]');
        const options = () => [...menu.querySelectorAll('[role="option"]:not([aria-disabled="true"])')];

        toggle.addEventListener('click', () => (menu.hidden ? this.openDropdown(dd) : this.closeDropdown(dd)));
        toggle.addEventListener('keydown', (e) => {
          if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
            e.preventDefault();
            this.openDropdown(dd);
          }
        });
        menu.addEventListener('click', (e) => {
          const option = e.target.closest('[role="option"]');
          if (option && option.getAttribute('aria-disabled') !== 'true') this.chooseOption(dd, option);
        });
        menu.addEventListener('keydown', (e) => {
          const list = options();
          const i = list.indexOf(document.activeElement);
          if (e.key === 'ArrowDown') { e.preventDefault(); list[Math.min(i + 1, list.length - 1)]?.focus(); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); list[Math.max(i - 1, 0)]?.focus(); }
          else if (e.key === 'Home') { e.preventDefault(); list[0]?.focus(); }
          else if (e.key === 'End') { e.preventDefault(); list[list.length - 1]?.focus(); }
          else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (list[i]) this.chooseOption(dd, list[i]); }
          else if (e.key === 'Escape') { e.preventDefault(); this.closeDropdown(dd, true); }
          else if (e.key === 'Tab') this.closeDropdown(dd);
        });
      });
      document.addEventListener('click', (e) => {
        this.dropdowns.forEach((dd) => { if (!dd.contains(e.target)) this.closeDropdown(dd); });
      });
    }

    openDropdown(dd) {
      this.dropdowns.forEach((other) => other !== dd && this.closeDropdown(other));
      const menu = dd.querySelector('[data-rr-dd-menu]');
      menu.hidden = false;
      dd.classList.add('is-open');
      dd.querySelector('[data-rr-dd-toggle]').setAttribute('aria-expanded', 'true');
      const target = menu.querySelector('[aria-selected="true"]') || menu.querySelector('[role="option"]:not([aria-disabled="true"])');
      target?.focus({ preventScroll: true });
    }

    closeDropdown(dd, refocus = false) {
      const menu = dd.querySelector('[data-rr-dd-menu]');
      if (!menu || menu.hidden) return;
      menu.hidden = true;
      dd.classList.remove('is-open');
      const toggle = dd.querySelector('[data-rr-dd-toggle]');
      toggle.setAttribute('aria-expanded', 'false');
      if (refocus) toggle.focus();
    }

    chooseOption(dd, option) {
      dd.querySelectorAll('[role="option"]').forEach((o) => o.setAttribute('aria-selected', String(o === option)));
      const value = dd.querySelector('[data-rr-dd-value]');
      const sub = dd.querySelector('[data-rr-dd-sub]');
      if (value) value.textContent = option.dataset.label;
      if (sub) {
        sub.textContent = option.dataset.sub || '';
        sub.hidden = !option.dataset.sub;
      }
      this.closeDropdown(dd, true);
      const select = dd.querySelector('select');
      if (select && select.value !== option.dataset.value) {
        select.value = option.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* ---------- Tabs ---------- */
    initTabs() {
      const tabs = [...this.querySelectorAll('[data-rr-tab]')];
      if (!tabs.length) return;
      const select = (tab) => {
        tabs.forEach((t) => {
          const active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', String(active));
          const panel = this.querySelector(`[data-rr-panel="${t.dataset.rrTab}"]`);
          if (panel) panel.hidden = !active;
        });
      };
      tabs.forEach((t) => t.addEventListener('click', () => select(t)));
      select(tabs[0]);
    }

    /* ---------- Frequently bought together ---------- */
    initFbt() {
      this.fbt = this.querySelector('[data-rr-fbt]');
      if (!this.fbt) return;
      this.fbt.querySelectorAll('.rr-fbt__check').forEach((cb) =>
        cb.addEventListener('change', () => {
          cb.closest('[data-rr-fbt-card]').classList.toggle('is-checked', cb.checked);
          this.updateFbtTotal();
        })
      );
      const addBtn = this.fbt.querySelector('[data-rr-fbt-add]');
      addBtn.addEventListener('click', () => {
        const items = [...this.fbt.querySelectorAll('.rr-fbt__check:checked')].map((cb) => ({
          id: Number(cb.value),
          quantity: 1,
        }));
        if (items.length) this.addItems(items, false, addBtn);
      });
      this.updateFbtTotal();
    }

    syncFbtVariants(sachets) {
      if (!this.fbt || !sachets) return;
      this.fbt.querySelectorAll('[data-rr-fbt-card]').forEach((card) => {
        const p = this.data.products.find((x) => String(x.id) === card.dataset.productId);
        if (!p) return;
        const v = p.variants.find((x) => x.available && x.sachets === sachets);
        if (!v) return;
        const cb = card.querySelector('.rr-fbt__check');
        cb.value = v.id;
        cb.dataset.price = v.price;
        card.querySelector('[data-rr-fbt-price]').textContent = this.money(v.price);
        const meta = card.querySelector('[data-rr-fbt-meta]');
        meta.textContent = [`${v.sachets}-Day Ritual`, meta.dataset.benefit].filter(Boolean).join(' · ');
      });
      this.updateFbtTotal();
    }

    updateFbtTotal() {
      const checked = [...this.fbt.querySelectorAll('.rr-fbt__check:checked')];
      const total = checked.reduce((sum, cb) => sum + Number(cb.dataset.price), 0);
      const n = checked.length;
      this.fbt.querySelector('[data-rr-fbt-total]').textContent = this.money(total);
      this.fbt.querySelector('[data-rr-fbt-count-label]').textContent = `Total for ${n} item${n === 1 ? '' : 's'}`;
      const btn = this.fbt.querySelector('[data-rr-fbt-add]');
      btn.textContent = `Add ${n} item${n === 1 ? '' : 's'} to cart`;
      btn.disabled = n === 0;
    }

    /* ---------- Sticky mobile bar ---------- */
    initSticky() {
      const bar = this.querySelector('[data-rr-sticky]');
      if (!bar || !this.addButton || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
      }).observe(this.addButton);
      bar.querySelector('[data-rr-sticky-add]')?.addEventListener('click', (e) =>
        this.addItems([this.mainItem()], false, e.currentTarget)
      );
    }

    /* ---------- Cart ---------- */
    async addItems(items, buyNow, button) {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items };
      const sections = cart?.getSectionsToRender ? cart.getSectionsToRender().map((s) => s.id) : [];
      // Request the cart drawer HTML for Buy now too: its Check Out button is what Magic Checkout listens for
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }
      this.showError('');
      button?.classList.add('is-loading');
      try {
        const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          this.showError(data.description || data.message || 'Could not add to cart.');
          return;
        }
        if (buyNow) {
          // Hand off to Razorpay Magic Checkout via the cart drawer's Check Out button.
          // Refresh the drawer first (without opening it) so the button exists even if the cart was empty.
          this.refreshCart(cart, data, sections, false);
          const checkout =
            (cart && cart.querySelector('[name="checkout"]')) || document.querySelector('[name="checkout"]');
          if (checkout) checkout.click();
          else window.location.href = '/cart';
          return;
        }
        this.refreshCart(cart, data, sections, true);
      } catch (err) {
        this.showError('Could not add to cart. Please try again.');
      } finally {
        button?.classList.remove('is-loading');
      }
    }

    refreshCart(cart, data, sections, openDrawer) {
      if (cart && data.sections) {
        sections.forEach((id) => {
          const el = document.getElementById(id);
          if (!el || !data.sections[id]) return;
          const doc = new DOMParser().parseFromString(data.sections[id], 'text/html');
          el.innerHTML = (doc.getElementById(id) || doc.body).innerHTML;
        });
        cart.cartAction?.();
      }
      fetch('/cart.js')
        .then((r) => r.json())
        .then((c) => {
          document.querySelectorAll('.cart-count').forEach((el) => {
            el.textContent = el.classList.contains('cart-count-drawer') ? `(${c.item_count})` : c.item_count;
          });
          document.querySelector('header-total-price')?.updateTotal?.(c);
          document.querySelector('free-ship-progress-bar')?.init?.(c.items_subtotal_price);
        })
        .catch(() => {});
      if (!openDrawer) return;
      if (cart?.open) cart.open();
      else window.location.href = '/cart';
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }
  }

  customElements.define('rr-pdp', RRPdp);

  // Magic Checkout Buy now hook (same as in product-item.liquid)
  window.customMagicAddToCart = async (event) => {
    const target = event && event.target;
    const cardForm = target?.closest?.('.ritual-buy-now-form');
    const pdp = !cardForm && (target?.closest?.('rr-pdp') || document.querySelector('rr-pdp'));
    let items;
    if (cardForm) {
      items = [{ id: Number(cardForm.querySelector('[name="id"]').value), quantity: 1 }];
    } else if (pdp?.mainItem) {
      items = [pdp.mainItem()];
    } else {
      throw new Error('Nothing to add');
    }
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await res.json();
    if (!res.ok || data.status) {
      pdp?.showError?.(data.description || data.message || 'Could not add to cart.');
      throw new Error(data.description || 'add failed');
    }
    return data;
  };
})();
